import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import { pad2 } from '@/shared/lib/pad2';
import { onAltArrowDownOpen } from '../../utils';
import { RClose } from './icons';

const MONTHS = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
];

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

/** Значение поля — строка datetime-local («2026-09-05T21:52»). */
type Parsed = { y: number; m: number; d: number; h: number; min: number };

function parse(value: string): Parsed | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(
    String(value ?? '').trim()
  );
  if (!m) {
    return null;
  }
  return {
    y: Number(m[1]),
    m: Number(m[2]) - 1,
    d: Number(m[3]),
    h: Number(m[4]),
    min: Number(m[5]),
  };
}

function compose(y: number, m: number, d: number, h: number, min: number) {
  return `${y}-${pad2(m + 1)}-${pad2(d)}T${pad2(h)}:${pad2(min)}`;
}

function nowValue() {
  const d = new Date();
  return compose(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    d.getHours(),
    d.getMinutes()
  );
}

function view(value: string, placeholder: string) {
  const p = parse(value);
  if (!p) {
    return placeholder;
  }
  return `${pad2(p.d)}.${pad2(p.m + 1)}.${p.y} ${pad2(p.h)}:${pad2(p.min)}`;
}

const SLOTS: { h: number; min: number }[] = [];
for (let h = 0; h <= 23; h++) {
  SLOTS.push({ h, min: 0 }, { h, min: 30 });
}

/** Поле даты со временем: подчёркнутое значение + оверлей с календарём и
 *  колонкой получасовых слотов (как в макете). */
export function RDateTimeField({
  title,
  value,
  onChange,
  disabled,
  initToNow = false,
  placeholder = '—',
  width = 128,
}: {
  title: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** Пустое значение при монтировании заполнить текущим временем. */
  initToNow?: boolean;
  placeholder?: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Parsed | null>(null);
  const [viewMonth, setViewMonth] = useState(() => {
    const p = parse(value) ?? parse(nowValue())!;
    return { y: p.y, m: p.m };
  });

  useEffect(() => {
    if (disabled || !initToNow) {
      return;
    }
    if (String(value ?? '').trim()) {
      return;
    }
    onChange(nowValue());
    // Автозаполнение — разово при монтировании, как в прежнем поле срока.
  }, []);

  const openPicker = () => {
    if (disabled) {
      return;
    }
    const p = parse(value);
    const at = p ?? parse(nowValue())!;
    setDraft(p);
    setViewMonth({ y: at.y, m: at.m });
    setOpen(true);
  };

  const cells = useMemo(() => {
    const first = new Date(viewMonth.y, viewMonth.m, 1);
    const shift = (first.getDay() + 6) % 7;
    const total = new Date(viewMonth.y, viewMonth.m + 1, 0).getDate();
    const out: (number | null)[] = [];
    for (let i = 0; i < shift; i++) {
      out.push(null);
    }
    for (let d = 1; d <= total; d++) {
      out.push(d);
    }
    return out;
  }, [viewMonth]);

  const shiftMonth = (delta: number) =>
    setViewMonth((v) => {
      const next = new Date(v.y, v.m + delta, 1);
      return { y: next.getFullYear(), m: next.getMonth() };
    });

  const dayPicked =
    draft && draft.y === viewMonth.y && draft.m === viewMonth.m
      ? draft.d
      : null;

  const apply = () => {
    if (!draft) {
      setOpen(false);
      return;
    }
    onChange(compose(draft.y, draft.m, draft.d, draft.h, draft.min));
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={openPicker}
        onKeyDown={onAltArrowDownOpen(open, openPicker)}
        style={{ flex: `0 0 ${width}px`, width }}
        className={clsx(
          'border-b border-[rgba(59,77,127,0.3)] pb-[4px] pt-[3px] text-center',
          'text-[12px] font-semibold text-[#141E31] transition-colors',
          disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-[#57B792]'
        )}
      >
        {view(value, placeholder)}
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-[500] flex items-center justify-center bg-[rgba(20,30,49,0.32)] p-[20px]"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-[320px] rounded-[18px] bg-white px-[18px] pb-[18px] pt-[16px] shadow-[0_24px_60px_rgba(20,30,49,0.3)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={title}
          >
            <div className="flex items-center gap-[10px]">
              <span className="flex-1 text-[12px] font-bold text-[#141E31]">
                {title}
              </span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-[26px] w-[26px] items-center justify-center rounded-full hover:bg-[rgba(59,77,127,0.08)]"
                aria-label="Закрыть"
              >
                <RClose />
              </button>
            </div>

            <div className="mt-[12px] flex overflow-hidden rounded-[12px] shadow-[0_1px_3px_rgba(20,30,49,0.08)]">
              <div className="w-[232px] px-[12px] pb-[10px] pt-[12px]">
                <div className="flex items-center gap-[8px]">
                  <button
                    type="button"
                    onClick={() => shiftMonth(-1)}
                    className="h-[22px] w-[22px] rounded-[7px] bg-[rgba(46,62,106,0.07)] text-[12px] leading-none text-[#575757]"
                    aria-label="Предыдущий месяц"
                  >
                    ‹
                  </button>
                  <div className="flex-1 text-center text-[12px] font-bold text-[#575757]">
                    {MONTHS[viewMonth.m]} {viewMonth.y}
                  </div>
                  <button
                    type="button"
                    onClick={() => shiftMonth(1)}
                    className="h-[22px] w-[22px] rounded-[7px] bg-[rgba(46,62,106,0.07)] text-[12px] leading-none text-[#575757]"
                    aria-label="Следующий месяц"
                  >
                    ›
                  </button>
                </div>

                <div className="mt-[10px] grid grid-cols-7 gap-[2px]">
                  {WEEKDAYS.map((w) => (
                    <div
                      key={w}
                      className="pb-[4px] text-center text-[10px] font-bold uppercase text-[rgba(44,49,60,0.6)]"
                    >
                      {w}
                    </div>
                  ))}
                  {cells.map((d, i) =>
                    d === null ? (
                      <span key={`e${i}`} className="h-[26px]" />
                    ) : (
                      <button
                        key={d}
                        type="button"
                        onClick={() =>
                          setDraft((prev) => ({
                            y: viewMonth.y,
                            m: viewMonth.m,
                            d,
                            h: prev?.h ?? 9,
                            min: prev?.min ?? 0,
                          }))
                        }
                        className={clsx(
                          'h-[26px] rounded-[7px] text-[11px] font-semibold',
                          dayPicked === d
                            ? 'bg-[#3B4D7F] text-white'
                            : 'text-[#141E31] hover:bg-[rgba(46,62,106,0.10)]'
                        )}
                      >
                        {d}
                      </button>
                    )
                  )}
                </div>

                <div className="mt-[10px] flex items-center gap-[8px] border-t border-[rgba(46,62,106,0.10)] pt-[10px]">
                  <button
                    type="button"
                    onClick={() => {
                      onChange('');
                      setOpen(false);
                    }}
                    className="text-[11px] font-semibold text-[#575757]"
                  >
                    Очистить
                  </button>
                  <button
                    type="button"
                    onClick={apply}
                    className="ml-auto rounded-[8px] bg-[#57B792] px-[12px] py-[6px] text-[11px] font-bold text-white"
                  >
                    Готово
                  </button>
                </div>
              </div>

              <div className="max-h-[268px] w-[88px] overflow-y-auto border-l border-[rgba(46,62,106,0.10)]">
                {SLOTS.map((s) => {
                  const active = draft?.h === s.h && draft?.min === s.min;
                  return (
                    <button
                      key={`${s.h}:${s.min}`}
                      type="button"
                      onClick={() =>
                        setDraft((prev) => {
                          const base = prev ?? {
                            ...(parse(nowValue()) as Parsed),
                          };
                          return { ...base, h: s.h, min: s.min };
                        })
                      }
                      className={clsx(
                        'block w-full border-b border-[rgba(46,62,106,0.07)] px-[10px] py-[7px] text-left text-[11px]',
                        active
                          ? 'bg-[rgba(87,183,146,0.16)] font-bold text-[#141E31]'
                          : 'font-medium text-[#575757] hover:bg-[rgba(46,62,106,0.08)]'
                      )}
                    >
                      {pad2(s.h)}:{pad2(s.min)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
